<template>
  <div id="settings-engineers-security-group">
    <div
      v-if="aclHelper.getSettingsUpdateAccess()"
      class="row controls"
    >
      <AddGroup :allGroups="headers" />
      <button
        v-if="!allowEdit"
        id="settings-engineers-security-group-edit"
        class="btn btn--orange"
        @click="enableEdit()"
      >
        {{ $t('action.edit') }}
      </button>
      <button
        v-if="allowEdit"
        id="settings-engineers-security-group-cancel"
        v-hkldreset
        class="btn btn--darkblue"
        @click="discardChange()"
      >
        {{ $t('action.cancel') }}
      </button>
      <button
        v-if="allowEdit"
        id="settings-engineers-security-group-save"
        v-hkldreset
        class="btn btn--orange"
        @click="save()"
      >
        {{ $t('action.save') }}
      </button>
    </div>

    <div id="settings-engineers-security-group-data">
      <table class="security-group-table setting-table">
        <thead>
          <th class="resource-header">
            {{ defaultHeader }}
          </th>
          <template v-for="(header, index) in headers">
            <th
              v-if="Math.ceil((index + 1) / headerPageSize) === headerPageNum"
              :key="index"
            >
              <a
                :id="'settings-engineers-security-group-role-' + index"
                @click="navToDetail(header.id)"
              >
                {{ header.name }}
                <i
                  class="fa fa-external-link"
                  aria-hidden="true"
                />
              </a>
            </th>
          </template>

          <th style="min-width: 70px; padding: 0;">
            <button
              id="settings-engineers-security-group-right-arrow"
              type="button"
              class="btn icon right"
              :disabled="Math.ceil(headers.length / headerPageSize) <= headerPageNum"
              @click="changePage(true)"
            >
              &#9654;
            </button>
            <button
              id="settings-engineers-security-group-left-arrow"
              type="button"
              class="btn icon right"
              :disabled="headerPageNum <= 1"
              @click="changePage(false)"
            >
              &#9664;
            </button>
          </th>
        </thead>
        <tbody>
          <template v-for="(actions, resource, index) in sortedPermissions">
            <tr
              v-if="isEnabledFeature(resource)"
              :key="`${resource}_name_${index}`"
            >
              <td>
                <button
                  v-if="!collapse[resource]"
                  :id="'settings-engineers-security-group-collapse-' + resource"
                  class="collapse-icon"
                  @click="collapseItem(resource, true)"
                >
                  &#8722;
                </button>
                <button
                  v-if="collapse[resource]"
                  :id="'settings-engineers-security-group-expanse-' + resource"
                  class="collapse-icon"
                  @click="collapseItem(resource, false)"
                >
                  &#x2b;
                </button>

                {{ getName(resource) }}
              </td>
              <template v-for="(header, key) in headers">
                <td
                  v-if="Math.ceil((key + 1) / headerPageSize) === headerPageNum"
                  :key="`${header.id}_resource_${key}`"
                />
              </template>
              <td v-if="headers.length >= headerPageSize" />
            </tr>
            <template v-if="isEnabledFeature(resource) && !collapse[resource]">
              <tr
                v-for="(perms, action) in actions.permissions"
                :key="`${resource}_perm_${action}`"
              >
                <td class="action">
                  {{ renderedAction(resource, action) }}
                </td>
                <template v-for="(header, index2) in headers">
                  <td
                    v-if="Math.ceil((index2 + 1) / headerPageSize) === headerPageNum"
                    :key="`${index2}_${header.id}_${action}_${perms[header.id]}`"
                  >
                    <input
                      v-if="allowEdit || perms[header.id]"
                      :key="`${header.id}_${action}_${perms[header.id]}`"
                      v-model="perms[header.id]"
                      v-hkldinput
                      type="checkbox"
                      :disabled="disableCheckbox(index2, header.id, resource, action)"
                      @change="updateGrantedAccess(header.id, resource, action)"
                    >
                  </td>
                </template>
                <td />
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import HttpStatus from 'http-status-codes'
import lodash from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import HTTP from '@/utils/http'
import AddGroup from '@/modules/settings/engineers/AddGroup'
import mutationTypes from '@/store/mutation-types'
import { ActionNameI18nMap, FullActionTypes, GreenOperationActionTypeNames, Rcs, ResourceNameMap, aclHelper, newFullActionType } from '@/utils/aclHelper'
import { AdminRoleName, SysAdminRoleName, UserType, defaultSecurityGroups, featureSecurityGroupMapping, securityGroupFeatureMapping } from '@/utils/consts'
import cookie from '@/utils/cookie'
import notification from '@/utils/notification'

export default {
  components: {
    AddGroup,
  },
  data () {
    const windowWidth = $(window).width() - 80
    const maxCols = (windowWidth > 1024) ? Math.floor(windowWidth / 175) : 5
    const headerPageSize = cookie.get('settingColumnSize') || maxCols
    const defaultHeader = this.$i18n.t('settings.resourceAction')
    return {
      allowEdit: false,
      headerPageNum: 1,
      headerPageSize,
      collapse: {},
      defaultHeader,
      headers: [],
      permissions: {},
      resourceAttrs: {},
      permissionsToUpdate: {},
      ResourceNameMap,
      aclHelper,
    }
  },
  computed: {
    ...mapGetters('tables', ['pageNum']),
    ...mapGetters('regions', ['featureStatus']),
    sortedPermissions () {
      const permGroupKeysArr = Object.keys(this.permissionsToUpdate)
      return permGroupKeysArr.reduce((sortedResult, groupName) => {
        const order = Object.keys(ActionNameI18nMap[groupName])
        const groupPermission = this.permissionsToUpdate[groupName]
        const sortedKeys = Object.keys(groupPermission.permissions)
          .sort((key1, key2) => {
            return order.indexOf(key1) - order.indexOf(key2)
          })
        for (const key of sortedKeys) {
          lodash.set(sortedResult, [groupName, 'permissions', key],
            groupPermission.permissions[key],
          )
        }
        return sortedResult
      }, {})
    },
  },
  async mounted () {
    this.headerPageNum = this.pageNum
    // It record the pageNum when the user go to the security group detail,then the user go back list page, it will set pageNum to 1
    // If you don't do that, when the user enter this page from another entrance, it will display previous state
    if (this.pageNum !== 1) {
      this.setPageNumber(1)
    }
    window.addEventListener('resize', this.handleResize)
    await this.init()
    const tableWidth = document.getElementById('settings-engineers-security-group-data').offsetWidth

    this.maxCols = (tableWidth > 1024) ? Math.floor((tableWidth - 270) / 132) : 5
    this.headerPageSize = cookie.get('settingColumnSize') || this.maxCols
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapMutations('tables', {
      setPageNumber: mutationTypes.TABLE_SET_PAGE_NUMBER,
    }),
    isEnabledFeature (resource) {
      let isInEnabledFeature = defaultSecurityGroups.includes(resource)
      let isEnabledSecurityGroup = securityGroupFeatureMapping[resource]
        ? securityGroupFeatureMapping[resource].some((features) => features.every(f => this.featureStatus[f]))
        : true
      if (!isInEnabledFeature && isEnabledSecurityGroup) {
        for (let [feature, securityGroups] of Object.entries(featureSecurityGroupMapping)) {
          if (this.featureStatus[feature] && securityGroups.includes(resource)) {
            isInEnabledFeature = true
            break
          }
        }
      }
      return isInEnabledFeature && isEnabledSecurityGroup
    },

    /* @TODO should refactor the belows methods:
     * shouldDisabledTenantProjectReadDeposit
     * shouldDisabledTenantProjectReadInspection
     * shouldDisabledTenantProjectReadInsurance
     * shouldDisablePreviewFitOutLetter
     */
    shouldDisabledGenericMethod (targetRcsName, targetActionName, preValidActions) {
      return (id, resource, action) => {
        if (!id || !resource || !action) return false
        const targetRcsPermissions = this.permissionsToUpdate[targetRcsName].permissions
        const targetPermissionCheckbox = resource === targetRcsName && action === targetActionName
        const isTickedPreCheckbox = preValidActions.some(actionName => {
          return lodash.get(targetRcsPermissions, [actionName, id], false)
        })
        return targetPermissionCheckbox && isTickedPreCheckbox
      }
    },

    shouldDisabledTenantProjectRead (id, resource, action) {
      const WorkPermitActions = this.permissionsToUpdate[Rcs.WorkPermit].permissions
      const TenantProjectActions = this.permissionsToUpdate[Rcs.EngineerTenantProject].permissions
      const TenantProjectActionsWithoutReadTP = lodash.omit(TenantProjectActions, ['read:any:info'])
      return resource === Rcs.EngineerTenantProject &&
        action === FullActionTypes.readAnyInfo &&
      (Object.values(WorkPermitActions).some(actionObject => actionObject[id]) || Object.values(TenantProjectActionsWithoutReadTP).some(actionObject => actionObject[id]))
    },
    shouldDisabledTenantProjectReadDeposit (id, resource, action) {
      const tenantProjectPermissions = this.permissionsToUpdate[Rcs.EngineerTenantProject].permissions
      return resource === Rcs.EngineerTenantProject &&
        action === FullActionTypes.readAnyDeposit &&
        (tenantProjectPermissions[FullActionTypes.updateAnyResumeDeposit][id] || tenantProjectPermissions[FullActionTypes.updateAnyDeposit][id] || tenantProjectPermissions[FullActionTypes.updateAnyAccounting][id])
    },
    shouldDisabledTenantProjectReadInspection (id, resource, action) {
      const tenantProjectPermissions = this.permissionsToUpdate[Rcs.EngineerTenantProject].permissions
      return resource === Rcs.EngineerTenantProject &&
        action === FullActionTypes.readAnyInspection &&
        tenantProjectPermissions[FullActionTypes.updateAnyInspection][id]
    },
    shouldDisabledTenantProjectReadInsurance (id, resource, action) {
      const tenantProjectPermissions = this.permissionsToUpdate[Rcs.EngineerTenantProject].permissions
      return resource === Rcs.EngineerTenantProject &&
        action === FullActionTypes.readAnyInsurance &&
        tenantProjectPermissions[FullActionTypes.updateAnyInsurance][id]
    },
    shouldDisablePreviewFitOutLetter (id, resource, action) {
      const tenantProjectPermissions = this.permissionsToUpdate[Rcs.EngineerTenantProject].permissions
      return resource === Rcs.EngineerTenantProject &&
        action === FullActionTypes.updateAnyPreview &&
        tenantProjectPermissions[FullActionTypes.updateAnySend][id]
    },

    shouldDisablePreviewGreenFitOut (id, resource, action) {
      return this.shouldDisabledGenericMethod(Rcs.EngineerTenantProject, FullActionTypes.updateAnyPreviewGreen, [
        newFullActionType.UpdateAnySendGreenFitout,
        newFullActionType.UpdateAnyCommentOnGreenFitout,
      ])(id, resource, action)
    },
    shouldDisableReadGreenPoint (id, resource, action) {
      return this.shouldDisabledGenericMethod(Rcs.EngineerTenantProject, newFullActionType.UpdateAnyReadGreenPoint, [
        newFullActionType.UpdateAnyReviewGreenFitout,
        newFullActionType.UpdateAnyCommentOnGreenFitout,
      ])(id, resource, action)
    },

    shouldDisablePreviewGreenOperation (id, resource, action) {
      return this.shouldDisabledGenericMethod(Rcs.TenancyRecord, FullActionTypes.updateAnyPreviewGreenOperation, [
        FullActionTypes[GreenOperationActionTypeNames.updateAnyCommentGreenOperation],
        FullActionTypes[GreenOperationActionTypeNames.updateAnySendGreenOperation],
      ])(id, resource, action)
    },

    shouldDisableViewGreenOperationPoint (id, resource, action) {
      return this.shouldDisabledGenericMethod(Rcs.TenancyRecord, FullActionTypes.updateAnyReadOperationPoints, [
        FullActionTypes[GreenOperationActionTypeNames.updateAnyReviewGreenOperation],
        FullActionTypes[GreenOperationActionTypeNames.updateAnyCommentGreenOperation],
      ])(id, resource, action)
    },

    shouldDisableReadRegionalConfig (id, resource, action) {
      return this.shouldDisabledGenericMethod(
        Rcs.Settings,
        newFullActionType.ReadAnyRegional,
        [newFullActionType.UpdateAnyRegional],
      )(id, resource, action)
    },

    shouldDisableReadSettings (id, resource, action) {
      return this.shouldDisabledGenericMethod(
        Rcs.Settings,
        newFullActionType.ReadAnyBasic,
        [newFullActionType.ReadAnyRegional],
      )(id, resource, action)
    },

    executeAllDisabledMethods (id, resource, action) {
      const methods = [
        this.shouldDisabledTenantProjectRead,
        this.shouldDisabledTenantProjectReadDeposit,
        this.shouldDisabledTenantProjectReadInspection,
        this.shouldDisabledTenantProjectReadInsurance,
        this.shouldDisablePreviewFitOutLetter,
        this.shouldDisablePreviewGreenFitOut,
        this.shouldDisableReadGreenPoint,
        this.shouldDisablePreviewGreenOperation,
        this.shouldDisableViewGreenOperationPoint,
        this.shouldDisableReadRegionalConfig,
        this.shouldDisableReadSettings,
      ]
      return methods.some(method => {
        return method(id, resource, action)
      })
    },

    // If permissions have relationship, such as one is ticked another is also ticked automatically, it would better use disable not make the checkbox value to set true again.
    // If you must use the method of reassigning the v-model of the checkbox, you have to use $nextTick to delay the setting operation
    disableCheckbox (index, id, resource, action) {
      const headerNameIsAdmin = AdminRoleName.includes(this.headers[index].name)
      const headerNameIsSysAdmin = SysAdminRoleName.includes(this.headers[index].name)
      const actions = this.permissionsToUpdate[resource].permissions
      // The TP's read permission is read:any:info not read:any:*
      const shouldDisableResourceRead = (action === FullActionTypes.readAny || action === FullActionTypes.readAnyInfo) && this.shouldSetRead(id, actions)
      const shouldDisableTRRead = action === FullActionTypes.readAny && resource === Rcs.TenancyRecord && this.shouldSetTenancyRead(id, resource)
      const shouldDisableTRUpdate = action === FullActionTypes.updateAnyDetail && resource === Rcs.TenancyRecord && this.shouldSetUpdate(id, actions)
      const shouldDisableBasicDRRead = resource === Rcs.Directory && action === FullActionTypes.readAnyBasic && this.shouldSetDirectoryRead(id, actions)
      const boolDisableMethodsResult = this.executeAllDisabledMethods(id, resource, action)
      return !this.allowEdit || headerNameIsAdmin || headerNameIsSysAdmin ||
      shouldDisableResourceRead || shouldDisableTRUpdate || shouldDisableTRRead || shouldDisableBasicDRRead || boolDisableMethodsResult
    },

    handleResponse (response) {
      if (response.status >= HttpStatus.INTERNAL_SERVER_ERROR) {
        notification.fireNotification(false, this.$i18n.t('error.system'))
        return null
      }
      if (response.status >= HttpStatus.BAD_REQUEST) {
        notification.fireNotification(false, response.data)
        return null
      }
      return response.data.data
    },
    handleServerError (error) {
      const response = error.response
      this.handleResponse(response)
    },

    async init () {
      await this.initRoles()
    },
    enableEdit () {
      this.permissions = JSON.parse(JSON.stringify(this.permissionsToUpdate))
      this.allowEdit = true
    },
    discardChange () {
      this.permissionsToUpdate = null
      this.permissionsToUpdate = Object.assign({}, this.permissions)
      this.allowEdit = false
    },

    async save () {
      const updatedRolePermissions = this.convertPermissionsToRoleBase(this.permissionsToUpdate)
      if (!updatedRolePermissions) {
        notification.fireNotification(false, this.$i18n.t('hint.emptyRequest'))
        return
      }
      try {
        const roles = JSON.stringify(updatedRolePermissions)
        const response = await HTTP.put('/roles/', { roles })
        this.handleResponse(response)
        this.permissions = JSON.parse(JSON.stringify(this.permissionsToUpdate))
        this.allowEdit = false
      } catch (err) {
        this.handleServerError(err)
      }
    },

    collapseItem (resource, collapse) {
      const newCollapses = this.collapse
      this.collapse = null
      newCollapses[resource] = collapse
      this.collapse = newCollapses
    },

    async initRoles () {
      let roles = []
      try {
        const response = await HTTP.get('/roles/')
        const rolesList = this.handleResponse(response)

        roles = lodash.cloneDeep(rolesList)
        const nonShowRoleMap = [{
          usertype: UserType.Staff.TenderAccount,
        }, {
          usertype: UserType.Staff.ProjectRep,
        }, {
          usertype: UserType.Staff.TenancyRep,
        }]
        for (const roleObj of nonShowRoleMap) {
          const index = roles.findIndex(item => item.usertype === roleObj.usertype)
          index > -1 && roles.splice(index, 1)
        }

        roles = lodash(roles).chain()
          .sortBy((role) => role.name).reverse()
          .sortBy((role) => { return [...AdminRoleName].includes(role.name) }).reverse()
          .value()
        roles.forEach(role => {
          role.name = role.name === AdminRoleName[0] ? this.$i18n.t('settings.security.systemAdmin') : role.name
        })
      } catch (err) {
        this.handleServerError(err)
        return
      }
      if (!roles) {
        return
      }
      const headers = []
      const updatedPermissions = {}
      for (let i = 0; i < roles.length; i += 1) {
        const role = roles[i]
        headers[i] = role
        this.updateRoleById(updatedPermissions, role)
      }
      for (let i = 0; i < roles.length; i += 1) {
        this.updateFullAccessForRole(updatedPermissions, roles[i].id)
      }
      this.headers = headers
      this.permissionsToUpdate = updatedPermissions
    },

    async updateRoleById (updatedPermissions, role) {
      if (!role.perms) {
        return
      }
      const permissionsObj = role.perms
      const permGroupKeysArr = Object.keys(permissionsObj)
      for (let i = 0; i < permGroupKeysArr.length; i += 1) {
        const strPermissionGroupKey = permGroupKeysArr[i]
        const actions = permissionsObj[strPermissionGroupKey]
        if (!updatedPermissions[strPermissionGroupKey]) {
          this.createPermissionGroup(updatedPermissions, strPermissionGroupKey, actions, role.id)
        }
        this.setGroupAccess(updatedPermissions[strPermissionGroupKey], actions, role.id)
        this.setResourceActionAttrs(strPermissionGroupKey, actions)
      }
    },

    setResourceActionAttrs (name, actions) {
      if (!this.resourceAttrs[name]) {
        this.resourceAttrs[name] = {}
      }
      const resource = this.resourceAttrs[name]
      Object.entries(actions).forEach(([actionName, attrs]) => {
        if (!resource[actionName]) {
          resource[actionName] = attrs
        } else {
          attrs.forEach(attr => {
            if (!resource[actionName].includes(attr)) {
              resource[actionName].push(attr)
            }
          })
        }
      })
    },

    createPermissionGroup (updatedPermissions, name, actions, roleId) {
      updatedPermissions[name] = {}
      const resource = updatedPermissions[name]
      this.collapse[name] = false
      resource.permissions = {}
      const permissions = resource.permissions
      const actionNames = Object.keys(actions)
      for (let i = 0; i < actionNames.length; i += 1) {
        const attrs = actions[actionNames[i]]
        attrs.forEach(attr => {
          const actionName = `${actionNames[i]}:${attr}`
          permissions[actionName] = { [roleId]: true }
        })
      }
    },

    setGroupAccess (permGroup = {}, items = {}, id) {
      const actions = permGroup.permissions
      const actionNames = []
      Object.entries(items).forEach(([actionName, attrs]) => {
        attrs.forEach(attr => {
          actionNames.push(`${actionName}:${attr}`)
        })
      })
      actionNames.forEach(actionName => {
        if (!actions[actionName]) {
          actions[actionName] = {}
        }
        actions[actionName][id] = true
      })
    },

    updateFullAccessForRole (permissions, roleId) {
      const permGroups = Object.keys(permissions)
      for (let i = 0; i < permGroups.length; i += 1) {
        const resource = permissions[permGroups[i]]
        if (resource && resource.permissions) {
          const actions = resource.permissions
          const keys = Object.keys(actions)
          for (let k = 0; k < keys.length; k += 1) {
            const action = actions[keys[k]]
            if (!action[roleId]) {
              action[roleId] = false
            }
          }
        }
      }
    },

    convertPermissionsToRoleBase (permissionsToUpdate = {}) {
      if (!permissionsToUpdate) {
        return null
      }

      const roles = []
      for (let i = 0; i < this.headers.length; i += 1) {
        const id = this.headers[i].id
        const role = {id}
        const perms = lodash.cloneDeep(this.resourceAttrs)
        Object.entries(perms).forEach(([resourceName, actions]) => {
          Object.entries(actions).forEach(([action, attrs]) => {
            const userAttrs = []
            attrs.forEach(attr => {
              if (permissionsToUpdate[resourceName].permissions[`${action}:${attr}`][id]) {
                userAttrs.push(attr)
              }
            })
            if (userAttrs.length > 0) {
              perms[resourceName][action] = userAttrs
            } else {
              delete perms[resourceName][action]
            }
          })
          if (Object.keys(perms[resourceName]).length <= 0) {
            delete perms[resourceName]
          }
        })
        role.perms = perms
        roles.push(role)
      }
      return roles
    },

    shouldSetRead (id, actions) {
      const regex = /^(update|create|delete):any:.+$/
      return Object.entries(actions).some(([actionName, actionObject]) =>
        actionObject[id] && actionName !== FullActionTypes.updateAnyManageECircularContact && regex.test(actionName))
    },

    shouldSetUpdate (id, actions) {
      return Object.entries(actions).some(([actionName, actionObject]) =>
        actionObject[id] && actionName === FullActionTypes.createAny)
    },

    shouldSetTenancyRead (id, resource, actions) {
      if (resource === Rcs.Hotb || resource === Rcs.TenancyRecord) {
        const hotbActions = this.permissionsToUpdate[Rcs.Hotb].permissions
        return Object.values(hotbActions).some(actionObject => actionObject[id])
      }
      return false
    },

    unCheckForTendersProjects (id, resource, action, actions) {
      this.$nextTick(() => {
        if (actions[action][id]) {
          if (resource === Rcs.Tender) {
            if (action === FullActionTypes.createOwn) {
              actions[FullActionTypes.readAny][id] = false
            } else if (action === FullActionTypes.readAny) {
              actions[FullActionTypes.createOwn][id] = false
            }
          } else if (resource === Rcs.Project) {
            if (action === newFullActionType.ReadOwnExportViewAll) return
            actions[FullActionTypes.readAny][id] = (action !== FullActionTypes.updateOwn)
            actions[FullActionTypes.updateOwn][id] = (action !== FullActionTypes.readAny)
          }
        } else if (resource === Rcs.Tender && action === FullActionTypes.updateAny) {
          actions[FullActionTypes.readAny][id] = false
        }
      })
    },
    shouldSetDirectoryRead (id, actions) {
      const shouldSetBasicRead = Object.entries(actions).some(([actionName, actionObject]) =>
        actionObject[id] && actionName !== FullActionTypes.readAnyBasic && actionName !== FullActionTypes.readDashboard)
      return shouldSetBasicRead
    },

    checkReadIfNeeded (id, resource, actions) {
      const result = this.shouldSetRead(id, actions)
      if (result && actions[FullActionTypes.readAny]) {
        actions[FullActionTypes.readAny][id] = true
      }
    },

    checkTenancy (id, resource, action, actions) {
      if (this.shouldSetTenancyRead(id, resource, actions)) {
        const tenancyRecordActions = this.permissionsToUpdate[Rcs.TenancyRecord].permissions
        tenancyRecordActions[FullActionTypes.readAny][id] = true
      }
      const targetRcsName = Rcs.TenancyRecord
      if (resource !== targetRcsName) return
      const autoTickMap = {
        [GreenOperationActionTypeNames.updateAnyPreviewGreenOperation]: [
          GreenOperationActionTypeNames.updateAnySendGreenOperation,
          GreenOperationActionTypeNames.updateAnyCommentGreenOperation,
        ],
        [GreenOperationActionTypeNames.updateAnyReadOperationPoints]: [
          GreenOperationActionTypeNames.updateAnyReviewGreenOperation,
          GreenOperationActionTypeNames.updateAnyCommentGreenOperation,
        ],
        updateAnyDetail: ['createAny'],
      }
      this.$nextTick(() => {
        Object.entries(autoTickMap).forEach(([targetAction, dependencyActions]) => {
          const shouldTick = dependencyActions.some(acKey => actions[FullActionTypes[acKey]][id])
          if (shouldTick) {
            actions[FullActionTypes[targetAction]][id] = true
          }
        })
      })
    },

    checkDirectory (id, resource, actions) {
      if (resource === Rcs.Directory) {
        const readAnyBasic = FullActionTypes.readAnyBasic
        const shouldSetBasicRead = this.shouldSetDirectoryRead(id, actions)
        if (shouldSetBasicRead) {
          this.$nextTick(() => {
            actions[readAnyBasic][id] = true
          })
        }
      }
    },

    checkTenantProject (id, resource, action, actions) {
      const shouldSetRead = Object.values(actions).some(actionObject => actionObject[id])
      const readAnyInfo = FullActionTypes.readAnyInfo
      if (resource === Rcs.EngineerTenantProject) {
        const autoTickMap = {
          [newFullActionType.UpdateAnyPreviewGreenFitout]: [
            newFullActionType.UpdateAnyCommentOnGreenFitout,
            newFullActionType.UpdateAnySendGreenFitout,
          ],
          [newFullActionType.UpdateAnyReadGreenPoint]: [
            newFullActionType.UpdateAnyReviewGreenFitout,
            newFullActionType.UpdateAnyCommentOnGreenFitout,
          ],
        }
        this.$nextTick(() => {
          Object.entries(autoTickMap).forEach(([targetAction, dependencyActions]) => {
            const shouldTick = dependencyActions.some(acKey => {
              return actions[acKey][id]
            })
            if (shouldTick) {
              actions[targetAction][id] = true
            }
          })
          if (shouldSetRead) {
            actions[readAnyInfo][id] = true
          }
          if (actions[FullActionTypes.updateAnyResumeDeposit][id]) {
            actions[FullActionTypes.readAnyDeposit][id] = true
          }
          if (actions[FullActionTypes.updateAnyDeposit][id] || actions[FullActionTypes.updateAnyAccounting][id]) {
            actions[FullActionTypes.readAnyDeposit][id] = true
          }
          if (actions[FullActionTypes.updateAnyInspection][id]) {
            actions[FullActionTypes.readAnyInspection][id] = true
          }
          if (actions[FullActionTypes.updateAnyInsurance][id]) {
            actions[FullActionTypes.readAnyInsurance][id] = true
          }
          if (actions[FullActionTypes.updateAnySend][id]) {
            actions[FullActionTypes.updateAnyPreview][id] = true
          }
        })
      }
    },

    checkWorkPermit (id, resource) {
      if (resource === Rcs.EngineerTenantProject || resource === Rcs.WorkPermit) {
        const WorkPermitActions = this.permissionsToUpdate[Rcs.WorkPermit].permissions
        const EngineerTenantProjectActions = this.permissionsToUpdate[Rcs.EngineerTenantProject].permissions
        const shouldSetTenantProjectRead = Object.values(WorkPermitActions).some(actionObject => actionObject[id])
        if (shouldSetTenantProjectRead) {
          this.$nextTick(() => {
            EngineerTenantProjectActions[FullActionTypes.readAnyInfo][id] = true
          })
        }
      }
    },

    checkSettings (id, resource) {
      const autoTickMap = {
        [newFullActionType.UpdateAnyBasic]: [
          newFullActionType.ReadAnyBasic,
        ],
        [newFullActionType.UpdateAnyRegional]: [
          newFullActionType.ReadAnyRegional,
          newFullActionType.ReadAnyBasic,
        ],
        [newFullActionType.ReadAnyRegional]: [
          newFullActionType.ReadAnyBasic,
        ],
      }
      this.autoTickGenericMethod(Rcs.Settings, autoTickMap, { id, resource })
    },

    // All the auto-tick logic should put into the autoTickMap variable.
    autoTickGenericMethod (targetResource, autoTickMap, { id, resource }) {
      if (targetResource !== resource) return
      const actions = this.permissionsToUpdate[resource].permissions
      this.$nextTick(() => {
        Object.entries(autoTickMap).forEach(([clickedAcKey, autoTickedAcKeys]) => {
          const shouldTriggerAuto = actions[clickedAcKey][id]
          if (shouldTriggerAuto) {
            autoTickedAcKeys.forEach(acKey => { actions[acKey][id] = true })
          }
        })
      })
    },

    updateGrantedAccess (id, resource, action) {
      const permissions = this.permissionsToUpdate
      const access = permissions[resource]
      const actions = access.permissions
      this.unCheckForTendersProjects(id, resource, action, actions)
      this.checkTenancy(id, resource, action, actions)
      this.checkReadIfNeeded(id, resource, actions)
      this.checkTenantProject(id, resource, action, actions)
      this.checkDirectory(id, resource, actions)
      this.checkWorkPermit(id, resource)
      this.checkSettings(id, resource)
      this.permissionsToUpdate = permissions
    },
    navToDetail (id) {
      if (id) {
        this.$router.push({
          name: 'security-group-detail',
          params: {
            id,
          },
        })
        this.setPageNumber(this.headerPageNum)
      }
    },
    changePage (isNextPage) {
      if (isNextPage) {
        this.headerPageNum += 1
      } else {
        this.headerPageNum -= 1
      }
    },
    renderedAction (resource, action) {
      let name = this.$t((ActionNameI18nMap[resource] || {})[action]) || action
      return name.split('/').join(' / ')
    },

    handleResize () {
      const tableWidth = document.getElementById('settings-engineers-security-group-data').offsetWidth
      this.maxCols = (tableWidth > 1024) ? Math.floor((tableWidth - 270) / 132) : 5
      this.headerPageSize = cookie.get('settingColumnSize') || this.maxCols
    },
    getName (resource) {
      let name = this.$t(ResourceNameMap[resource]) || resource
      return name.split('/').join(' / ')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';

.security-group-table thead th:first-child {
  min-width: 285px;
}

#settings-engineers-security-group-data {
  padding: 15px 24px;
  td:first-child {
    word-break: break-word !important;
  }
  .setting-table tbody tr td:first-child {
    min-width: 285px;
  }
}
tr:nth-child(odd) {
  background-color: #fff!important;
}
tr:nth-child(even) {
  background-color: #f1f1f1!important;
}

th:not(:first-child), td:not(:first-child) {
  text-align: center;
}
th {
  padding: 4px;
}

.action {
  padding-left: 30px;
  text-transform: capitalize;
}

.collapse-icon {
  width: 15px;
  background: #D9D8D8;
  color: #ffffff;
  text-align: center;
  vertical-align: text-top;
  padding-bottom: 2px;
  border-radius: 2px;
}

.controls {
  margin-right: 24px;
}
</style>
